import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import './SocialFeed.css';
import { SOCIAL_FEED_FALLBACK_POSTS } from '../data/socialFeedFallback';
import SocialFollowBadges, { SOCIAL_PROFILE_URLS } from './SocialFollowBadges';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  SocialPlatformIcon,
} from './socialPlatformIcons';

const INSTAGRAM_URL = SOCIAL_PROFILE_URLS.instagram;
const FACEBOOK_URL = SOCIAL_PROFILE_URLS.facebook;
const LINKEDIN_URL = SOCIAL_PROFILE_URLS.linkedin;

/** api-only = never show curated fallback (empty feed → platform link cards). fallback-only = skip API. */
const socialFeedMode = (process.env.REACT_APP_SOCIAL_FEED_MODE || '').toLowerCase();

/** Auto-advance interval (WhatsApp / IG-style status) */
const STORY_SLIDE_MS = 6500;

/** LinkedIn iframe posts need longer on-screen time to read */
const LINKEDIN_STORY_SLIDE_MS = 10000;

/** Same host rules as backend `socialBlobProxyUrl` — private blobs must load via API proxy */
const BLOB_HOST_RE = /^[a-z0-9-]+\.(?:public\.)?blob\.vercel-storage\.com$/i;

const PUBLIC_BLOB_HOST_RE = /^[a-z0-9-]+\.public\.blob\.vercel-storage\.com$/i;

/**
 * Private Vercel Blob URLs → `{API}/api/content/social-blob?url=…`.
 * Public store URLs (*.public.blob.vercel-storage.com) are returned as-is (no proxy).
 */
function socialFeedMediaSrc(imageUrl) {
  if (!imageUrl) return imageUrl;
  const s = String(imageUrl).trim();
  if (!s) return s;
  if (/\/api\/content\/social-blob\?/.test(s)) return s;
  try {
    const u = new URL(s);
    if (PUBLIC_BLOB_HOST_RE.test(u.hostname)) return s;
    if (!BLOB_HOST_RE.test(u.hostname)) return s;
  } catch {
    return imageUrl;
  }
  const api = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  if (!api) return imageUrl;
  return `${api}/api/content/social-blob?url=${encodeURIComponent(s)}`;
}

function normalizeSocialPayload(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.posts)) return data.posts;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

function normalizeFeedPlatform(p) {
  const s = String(p || '').toLowerCase();
  if (s === 'facebook' || s === 'instagram' || s === 'linkedin') return s;
  return s;
}

/** Paste iframe or URL — same rules as admin `normalizeLinkedInEmbedUrl`. */
function extractLinkedInEmbedSrc(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/<iframe/i.test(s)) {
    const m = s.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    return m && m[1] ? m[1].trim() : '';
  }
  return s;
}

function normalizeLinkedInEmbedUrl(raw) {
  const src = extractLinkedInEmbedSrc(raw);
  if (!src) return '';
  try {
    const u = new URL(src.startsWith('http') ? src : `https://${src}`);
    const host = u.hostname.toLowerCase();
    if (host === 'linkedin.com' || host.endsWith('.linkedin.com')) {
      u.hostname = 'www.linkedin.com';
    }
    if (u.hostname !== 'www.linkedin.com') return '';
    if (!u.pathname.includes('/embed/')) return '';
    u.searchParams.set('collapsed', u.searchParams.get('collapsed') || '1');
    u.searchParams.set('height', '400');
    return u.toString();
  } catch {
    return '';
  }
}

/**
 * Map API quirks (snake_case, missing mediaType) so curated LinkedIn embeds always render.
 */
function enrichApiSocialPosts(posts) {
  return (posts || []).map((post) => {
    const p = { ...post };
    const fromSnake = p.linkedin_embed_url;
    if (fromSnake && !p.linkedinEmbedUrl) p.linkedinEmbedUrl = fromSnake;
    p.platform = normalizeFeedPlatform(p.platform);
    if (p.platform === 'linkedin' && p.linkedinEmbedUrl) {
      const norm = normalizeLinkedInEmbedUrl(String(p.linkedinEmbedUrl));
      if (norm) {
        p.linkedinEmbedUrl = norm;
        if (!p.mediaType || p.mediaType === 'IMAGE') p.mediaType = 'EMBED';
      }
    }
    return p;
  });
}

/**
 * Instagram & Facebook: if postedAt is set, hide from storefront after 24h.
 * LinkedIn: no TTL — stays until you change it in admin.
 */
const CURATED_SOCIAL_STORY_TTL_MS = 24 * 60 * 60 * 1000;

function filterSocialPostsByStoryTtl(posts) {
  return (posts || []).filter((post) => {
    if (normalizeFeedPlatform(post?.platform) === 'linkedin') return true;
    const raw = post?.postedAt;
    if (raw == null || raw === '') return true;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return true;
    return Date.now() - t < CURATED_SOCIAL_STORY_TTL_MS;
  });
}

/** True when we have a normalized LinkedIn iframe src (embed paste or API field). */
function isSafeLinkedInEmbedUrl(url) {
  return Boolean(normalizeLinkedInEmbedUrl(url));
}

/** Show LinkedIn iframe when embed URL is valid — do not require mediaType (API may omit it). */
function hasLinkedInEmbed(post) {
  if (!post || normalizeFeedPlatform(post.platform) !== 'linkedin') return false;
  return isSafeLinkedInEmbedUrl(String(post.linkedinEmbedUrl || ''));
}

function isSocialPostVideo(post) {
  if (hasLinkedInEmbed(post)) return false;
  if (post.mediaType === 'EMBED') return false;
  if (post.mediaType === 'VIDEO') return true;
  const u = post.image || '';
  if (/\.(mp4|webm|mov)(\?|#|$)/i.test(u)) return true;
  try {
    const m = String(u).match(/[?&]url=([^&]+)/);
    if (m) {
      const inner = decodeURIComponent(m[1]);
      return /\.(mp4|webm|mov)(\?|#|$)/i.test(inner);
    }
  } catch {
    /* ignore */
  }
  return false;
}

function platformLabel(platform) {
  if (platform === 'linkedin') return 'LinkedIn';
  if (platform === 'facebook') return 'Facebook';
  if (platform === 'instagram') return 'Instagram';
  return String(platform || '').charAt(0).toUpperCase() + String(platform || '').slice(1);
}

function StoryRingThumb({ platform }) {
  return <SocialPlatformIcon platform={normalizeFeedPlatform(platform)} size={22} />;
}

const RING_VIEW_SIZE = 68;
const RING_CENTER = RING_VIEW_SIZE / 2;
const RING_RADIUS = 30;
const RING_SEGMENT_GAP_DEG = 4;

function pointOnStoryRing(cx, cy, r, degFromTop) {
  const rad = (degFromTop * Math.PI) / 180;
  return {
    x: cx + r * Math.sin(rad),
    y: cy - r * Math.cos(rad),
  };
}

function storyRingSegmentPath(index, total) {
  if (total <= 0) return '';
  if (total === 1) {
    const start = pointOnStoryRing(RING_CENTER, RING_CENTER, RING_RADIUS, 0);
    const mid = pointOnStoryRing(RING_CENTER, RING_CENTER, RING_RADIUS, 180);
    const end = pointOnStoryRing(RING_CENTER, RING_CENTER, RING_RADIUS, 359.99);
    return `M ${start.x} ${start.y} A ${RING_RADIUS} ${RING_RADIUS} 0 1 1 ${mid.x} ${mid.y} A ${RING_RADIUS} ${RING_RADIUS} 0 1 1 ${end.x} ${end.y}`;
  }

  const span = (360 - total * RING_SEGMENT_GAP_DEG) / total;
  const startDeg = index * (span + RING_SEGMENT_GAP_DEG);
  const endDeg = startDeg + span;
  const start = pointOnStoryRing(RING_CENTER, RING_CENTER, RING_RADIUS, startDeg);
  const end = pointOnStoryRing(RING_CENTER, RING_CENTER, RING_RADIUS, endDeg);
  const largeArc = span > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${RING_RADIUS} ${RING_RADIUS} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

function SegmentedStoryRing({ platform, posts, storyIndex, seenIds, onSelectStory }) {
  const plat = platform;
  const gradId = `social-story-ring-grad-${plat}`;

  return (
    <div
      className={`social-story-ring social-story-ring--unified social-story-ring--${plat} social-story-ring--active`}
      aria-label={`${platformLabel(plat)} stories, ${posts.length} ${posts.length === 1 ? 'post' : 'posts'}`}
    >
      <div className="social-story-ring-figure">
        <svg
          className="social-story-ring-segments-svg"
          viewBox={`0 0 ${RING_VIEW_SIZE} ${RING_VIEW_SIZE}`}
          aria-hidden
        >
          {plat === 'instagram' ? (
            <defs>
              <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f09433" />
                <stop offset="35%" stopColor="#e6683c" />
                <stop offset="65%" stopColor="#dc2743" />
                <stop offset="100%" stopColor="#bc1888" />
              </linearGradient>
            </defs>
          ) : null}
          {posts.map((post, i) => {
            const segId = postIdentity(post, i);
            const isActive = i === storyIndex;
            const isSeen = seenIds.has(segId);
            return (
              <path
                key={segId}
                d={storyRingSegmentPath(i, posts.length)}
                className={
                  'social-story-ring-segment' +
                  (isActive ? ' social-story-ring-segment--active' : '') +
                  (isSeen ? ' social-story-ring-segment--seen' : '')
                }
                stroke={plat === 'instagram' ? `url(#${gradId})` : undefined}
                onClick={() => onSelectStory(i)}
              />
            );
          })}
        </svg>
        <span className="social-story-ring-thumb social-story-ring-thumb--unified">
          <StoryRingThumb platform={plat} />
        </span>
      </div>
    </div>
  );
}

function isValidPostUrl(s) {
  if (!s || typeof s !== 'string') return false;
  try {
    const u = new URL(s.trim());
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

/** Per-browser “already watched” ids (not synced across users). */
const SEEN_STORAGE_KEY = 'raheasy.socialFeed.seenPostIds.v1';

function readSeenSet() {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function persistSeenSet(set) {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* quota / private mode */
  }
}

function postIdentity(post, index) {
  if (post && post.id != null && String(post.id).length > 0) return String(post.id);
  const img = post?.image ? String(post.image).slice(0, 160) : '';
  const emb = post?.linkedinEmbedUrl ? String(post.linkedinEmbedUrl).slice(0, 160) : '';
  return `idx-${index}-${img}-${emb}`;
}

function findNextUnseenFrom(fromIdx, postsList, seenSet) {
  const n = postsList.length;
  if (!n) return null;
  for (let step = 1; step <= n; step++) {
    const i = (fromIdx + step) % n;
    const pid = postIdentity(postsList[i], i);
    if (!seenSet.has(pid)) return i;
  }
  return null;
}

/** Unseen first, then continue in list order and loop (per-user carousel). */
function findNextAutoplayIndex(fromIdx, postsList, seenSet) {
  const n = postsList.length;
  if (!n) return null;
  const unseen = findNextUnseenFrom(fromIdx, postsList, seenSet);
  if (unseen !== null) return unseen;
  return (fromIdx + 1) % n;
}

/**
 * One platform column: per-browser story carousel.
 * Autoplay runs unseen posts first, then loops the full list again for that user.
 */
const PlatformStoryCarousel = memo(function PlatformStoryCarousel({
  platform,
  posts,
  profileUrl,
  seenIds,
  onMarkSeen,
}) {
  const [storyIndex, setStoryIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progressTick, setProgressTick] = useState(0);
  const videoRef = useRef(null);
  const seenIdsRef = useRef(seenIds);

  seenIdsRef.current = seenIds;

  const postsKey = posts.map((p, i) => postIdentity(p, i)).join('\u0001');

  useEffect(() => {
    if (!posts.length) {
      setStoryIndex(0);
      return;
    }
    const firstUnseen = posts.findIndex((p, i) => !seenIds.has(postIdentity(p, i)));
    setStoryIndex(firstUnseen >= 0 ? firstUnseen : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the post set changes
  }, [postsKey]);

  useEffect(() => {
    if (!posts.length || paused) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'hidden') setPaused(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [posts.length, paused]);

  useEffect(() => {
    if (!posts.length || paused) return undefined;
    const cur = posts[storyIndex];
    if (!cur) return undefined;
    const pid = postIdentity(cur, storyIndex);

    const slideMs = hasLinkedInEmbed(cur) ? LINKEDIN_STORY_SLIDE_MS : STORY_SLIDE_MS;

    const t = setTimeout(() => {
      const nextSeen = new Set(seenIdsRef.current);
      nextSeen.add(pid);
      onMarkSeen(pid);

      const nextIdx = findNextAutoplayIndex(storyIndex, posts, nextSeen);
      if (nextIdx !== null) {
        setStoryIndex(nextIdx);
      }
      setProgressTick((x) => x + 1);
    }, slideMs);
    return () => clearTimeout(t);
  }, [posts, storyIndex, paused, progressTick, onMarkSeen]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const cur = posts[storyIndex];
    if (cur && isSocialPostVideo(cur)) {
      v.play().catch(() => {});
    } else {
      v.pause?.();
    }
    return undefined;
  }, [storyIndex, posts]);

  const goPrev = useCallback(() => {
    if (!posts.length) return;
    const cur = posts[storyIndex];
    if (cur) onMarkSeen(postIdentity(cur, storyIndex));
    setStoryIndex((i) => (i - 1 + posts.length) % posts.length);
    setProgressTick((x) => x + 1);
  }, [posts, storyIndex, onMarkSeen]);

  const goNext = useCallback(() => {
    if (!posts.length) return;
    const cur = posts[storyIndex];
    if (cur) onMarkSeen(postIdentity(cur, storyIndex));
    setStoryIndex((i) => (i + 1) % posts.length);
    setProgressTick((x) => x + 1);
  }, [posts, storyIndex, onMarkSeen]);

  const plat = platform;
  const current = posts.length ? posts[Math.min(storyIndex, posts.length - 1)] : null;

  const selectStory = useCallback(
    (index) => {
      if (index < 0 || index >= posts.length) return;
      if (index !== storyIndex) {
        const prevPost = posts[storyIndex];
        if (prevPost) onMarkSeen(postIdentity(prevPost, storyIndex));
      }
      setStoryIndex(index);
      setProgressTick((x) => x + 1);
    },
    [posts, storyIndex, onMarkSeen]
  );

  const linkedInViewportRef = useRef(null);
  const [linkedInInView, setLinkedInInView] = useState(false);
  const [linkedInFrameLoaded, setLinkedInFrameLoaded] = useState(false);

  const linkedInEmbedSrc =
    platform === 'linkedin' && current && hasLinkedInEmbed(current)
      ? normalizeLinkedInEmbedUrl(String(current.linkedinEmbedUrl || ''))
      : '';

  useEffect(() => {
    setLinkedInFrameLoaded(false);
  }, [linkedInEmbedSrc]);

  useEffect(() => {
    if (!linkedInInView) setLinkedInFrameLoaded(false);
  }, [linkedInInView]);

  useLayoutEffect(() => {
    if (platform !== 'linkedin' || !posts.length) return undefined;
    const el = linkedInViewportRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const en of entries) {
          if (en.target === el) {
            setLinkedInInView(en.isIntersecting);
            break;
          }
        }
      },
      { root: null, rootMargin: '100px 0px 140px 0px', threshold: 0.06 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [platform, posts.length, postsKey]);

  if (!posts.length) {
    return (
      <div className={`social-platform-carousel-empty social-platform-carousel-empty--${plat}`}>
        <span className="social-platform-carousel-empty-icon" aria-hidden>
          {plat === 'instagram' && <InstagramIcon size={28} />}
          {plat === 'facebook' && <FacebookIcon size={28} />}
          {plat === 'linkedin' && <LinkedInIcon size={28} />}
        </span>
        <p>No {platformLabel(plat)} stories yet.</p>
        <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="social-platform-carousel-empty-link">
          Open {platformLabel(plat)}
        </a>
      </div>
    );
  }

  return (
    <div className={`social-platform-carousel social-platform-carousel--${plat}`}>
      <div className="social-story-rings-scroll social-story-rings-scroll--compact" aria-label={`${platformLabel(plat)} story ring`}>
        <div className="social-story-rings-inner social-story-rings-inner--unified">
          <SegmentedStoryRing
            platform={plat}
            posts={posts}
            storyIndex={storyIndex}
            seenIds={seenIds}
            onSelectStory={selectStory}
          />
        </div>
      </div>

      <div
        ref={plat === 'linkedin' ? linkedInViewportRef : undefined}
        className={`social-story-viewer social-story-viewer--compact social-story-viewer--${plat}`}
        role="region"
        aria-roledescription="carousel"
        aria-label={`${platformLabel(plat)} stories`}
      >
        <div className="social-story-progress" key={`${plat}-prog-${progressTick}-${storyIndex}`}>
          {posts.map((post, i) => {
            const segId = postIdentity(post, i);
            const segSeen = seenIds.has(segId);
            const isActive = i === storyIndex;
            const autoplayAnimates = isActive && !paused;
            const segMs = hasLinkedInEmbed(post) ? LINKEDIN_STORY_SLIDE_MS : STORY_SLIDE_MS;
            return (
              <div key={post.id || segId} className="social-story-progress-track">
                <div
                  className={
                    'social-story-progress-fill' +
                    (autoplayAnimates ? ' social-story-progress-fill--active' : '') +
                    (autoplayAnimates && segSeen ? ' social-story-progress-fill--replay' : '') +
                    (paused && autoplayAnimates ? ' social-story-progress-fill--paused' : '') +
                    (!autoplayAnimates && segSeen ? ' social-story-progress-fill--done social-story-progress-fill--seen' : '')
                  }
                  style={autoplayAnimates ? { animationDuration: `${segMs}ms` } : undefined}
                />
              </div>
            );
          })}
        </div>

        <div className="social-story-viewer-chrome">
          <span className="social-story-viewer-platform">{platformLabel(plat)}</span>
          <button
            type="button"
            className="social-story-pause-btn"
            onClick={(e) => {
              e.stopPropagation();
              setPaused((p) => {
                const next = !p;
                if (!next) setProgressTick((x) => x + 1);
                return next;
              });
            }}
            aria-pressed={paused}
          >
            {paused ? 'Play' : 'Pause'}
          </button>
        </div>

        {current &&
        (isValidPostUrl(current.url) || (plat === 'linkedin' && hasLinkedInEmbed(current))) ? (
          <a
            href={
              isValidPostUrl(current.url)
                ? current.url
                : normalizeLinkedInEmbedUrl(String(current.linkedinEmbedUrl || '')) || String(current.linkedinEmbedUrl || '').trim()
            }
            target="_blank"
            rel="noopener noreferrer"
            className="social-story-viewer-link"
          >
            Open link →
          </a>
        ) : null}

        <div
          className={
            'social-story-viewer-media-wrap' +
            (current && hasLinkedInEmbed(current) ? ' social-story-viewer-media-wrap--linkedin-embed' : '')
          }
        >
          {current &&
            (hasLinkedInEmbed(current) ? (
              <div className="social-linkedin-embed-frame">
                {!linkedInInView || !linkedInEmbedSrc ? (
                  <div className="social-linkedin-embed-placeholder" aria-hidden />
                ) : (
                  <>
                    {!linkedInFrameLoaded ? (
                      <div
                        className="social-linkedin-embed-skeleton"
                        aria-busy="true"
                        aria-label="Loading LinkedIn post"
                      />
                    ) : null}
                    <iframe
                      key={linkedInEmbedSrc}
                      title={current.caption || 'LinkedIn post'}
                      className={
                        'social-story-viewer-embed' +
                        (linkedInFrameLoaded ? ' social-story-viewer-embed--loaded' : ' social-story-viewer-embed--loading')
                      }
                      src={linkedInEmbedSrc}
                      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
                      loading="lazy"
                      onLoad={() => setLinkedInFrameLoaded(true)}
                      referrerPolicy="strict-origin-when-cross-origin"
                    />
                  </>
                )}
              </div>
            ) : isSocialPostVideo(current) && current.image ? (
              <video
                ref={videoRef}
                key={String(current.id) + '-v'}
                className="social-story-viewer-media"
                src={socialFeedMediaSrc(current.image)}
                muted
                playsInline
                loop
                preload="metadata"
                aria-label={current.caption || 'Story video'}
              />
            ) : (
              <img
                className="social-story-viewer-media"
                src={socialFeedMediaSrc(current.image) || '/RaheasY Main Logo.png'}
                alt={current.caption || ''}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = '/RaheasY Main Logo.png';
                }}
              />
            ))}
          {current ? (
            <div className="social-story-media-tap-grid" role="presentation">
              <button
                type="button"
                className="social-story-media-tap social-story-media-tap--prev"
                onClick={(e) => {
                  e.preventDefault();
                  goPrev();
                }}
                aria-label={`Previous ${platformLabel(plat)} story`}
              />
              {current.url && isValidPostUrl(current.url) ? (
                <a
                  href={current.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-story-media-tap social-story-media-tap--link"
                  aria-label={
                    current.caption
                      ? `Open link: ${String(current.caption).slice(0, 80)}`
                      : `Open ${platformLabel(plat)} link`
                  }
                />
              ) : plat === 'linkedin' && hasLinkedInEmbed(current) ? (
                <a
                  href={normalizeLinkedInEmbedUrl(String(current.linkedinEmbedUrl || ''))}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-story-media-tap social-story-media-tap--link"
                  aria-label="Open embedded LinkedIn post on LinkedIn"
                />
              ) : (
                <span className="social-story-media-tap social-story-media-tap--center" />
              )}
              <button
                type="button"
                className="social-story-media-tap social-story-media-tap--next"
                onClick={(e) => {
                  e.preventDefault();
                  goNext();
                }}
                aria-label={`Next ${platformLabel(plat)} story`}
              />
            </div>
          ) : null}
        </div>

        {current?.caption ? (
          <div className={`social-story-caption-bar social-story-caption-bar--${plat}`}>
            <p className="social-story-caption-text">{current.caption}</p>
            <time className="social-story-caption-time">
              {current.timestamp ? new Date(current.timestamp).toLocaleDateString() : ''}
            </time>
          </div>
        ) : null}
      </div>

      <div className="social-story-arrows social-story-arrows--compact">
        <button type="button" className="social-story-arrow" onClick={goPrev} aria-label={`Previous ${platformLabel(plat)} story`}>
          ‹
        </button>
        <button type="button" className="social-story-arrow" onClick={goNext} aria-label={`Next ${platformLabel(plat)} story`}>
          ›
        </button>
      </div>
    </div>
  );
});

const SocialFeed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seenPostIds, setSeenPostIds] = useState(() => readSeenSet());

  const markPostSeen = useCallback((id) => {
    if (!id) return;
    setSeenPostIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      persistSeenSet(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== SEEN_STORAGE_KEY || e.newValue == null) return;
      try {
        const arr = JSON.parse(e.newValue);
        setSeenPostIds(new Set(Array.isArray(arr) ? arr.map(String) : []));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    fetchSocialPosts();
  }, []);

  const fetchSocialPosts = async () => {
    const fallbackOnly = socialFeedMode === 'fallback-only';

    if (fallbackOnly) {
      setPosts(SOCIAL_FEED_FALLBACK_POSTS);
      setLoading(false);
      return;
    }

    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
    if (!apiUrl) {
      setPosts([]);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`${apiUrl}/api/social/posts`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (response.ok) {
        const data = await response.json();
        const list = filterSocialPostsByStoryTtl(enrichApiSocialPosts(normalizeSocialPayload(data)));
        setPosts(list);
      } else {
        setPosts([]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name !== 'AbortError') console.error('Error fetching social posts:', err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const instagramPosts = posts.filter((p) => normalizeFeedPlatform(p.platform) === 'instagram');
  const facebookPosts = posts.filter((p) => normalizeFeedPlatform(p.platform) === 'facebook');
  const linkedinPosts = posts.filter((p) => normalizeFeedPlatform(p.platform) === 'linkedin');
  const hasStoryPosts = instagramPosts.length > 0 || facebookPosts.length > 0 || linkedinPosts.length > 0;

  return (
    <section className="social-feed-section" id="sales">
      <div className="section-container">
        <div className="section-header section-header--social">
          <h2 className="section-title">Latest from our socials</h2>
        </div>

        <SocialFollowBadges />

        {!loading && hasStoryPosts ? (
          <div className="social-platform-carousels-row">
            {instagramPosts.length > 0 ? (
              <div className="social-platform-carousel-column">
                <h3 className="social-platform-carousel-heading">Instagram</h3>
                <PlatformStoryCarousel
                  platform="instagram"
                  posts={instagramPosts}
                  profileUrl={INSTAGRAM_URL}
                  seenIds={seenPostIds}
                  onMarkSeen={markPostSeen}
                />
                </div>
            ) : null}
            {facebookPosts.length > 0 ? (
              <div className="social-platform-carousel-column">
                <h3 className="social-platform-carousel-heading">Facebook</h3>
                <PlatformStoryCarousel
                  platform="facebook"
                  posts={facebookPosts}
                  profileUrl={FACEBOOK_URL}
                  seenIds={seenPostIds}
                  onMarkSeen={markPostSeen}
                />
              </div>
            ) : null}
            {linkedinPosts.length > 0 ? (
              <div className="social-platform-carousel-column">
                <h3 className="social-platform-carousel-heading">LinkedIn</h3>
                <PlatformStoryCarousel
                  platform="linkedin"
                  posts={linkedinPosts}
                  profileUrl={LINKEDIN_URL}
                  seenIds={seenPostIds}
                  onMarkSeen={markPostSeen}
                />
                </div>
            ) : null}
                </div>
        ) : null}
      </div>
    </section>
  );
};

export default SocialFeed;
